import { Divider, FormControl, FormHelperText, Grid, Stack, Typography, Box, Button } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { compareDates, isValidDate } from 'src/utils/date'
import { EditRRSchema } from './edit-rr.schema'
import { DatePicker } from '@mui/x-date-pickers-pro'
import { TextField } from '@mui/material'
import { Controller } from 'react-hook-form'
import ResourceTypeResourceSelect from '../../../../../../../components/resource-type-resource-select/resource-type-resource-select'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import ResettableTextField from 'src/app/pages/booking/reservation-insert/components/resettable-text-field'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { HicFormLabel } from 'src/app/components/form-fields/hic-form-label.styled'
import { ReservedResourcePhaseCell } from '../../commons/grid-cells'
import { PhaseSelect } from './components/phase-select'
import { DailyPrices } from 'src/app/components/daily-prices/daily-prices'
import { faScaleBalanced } from '@fortawesome/pro-light-svg-icons'
import { ResourceOccupancies } from './components/resource-occupancies'
import { displayCancellationPolicyType } from 'src/utils/booking-policy'
import { useEditRRDrawer, EditRRProps } from './edit-rr-drawer'
import HicTimeRangePicker from 'src/app/components/hic-date-time-range-picker/hic-time-range-picker'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { FormItem } from 'hic-components'

export default function EditTimeRR({ propertyId, reservedResource, open, isOnlyChild, onClose }: EditRRProps) {
  const { md } = useBreakpoints()
  const { t } = useTranslation()
  const hotelDate = useHotelDate()
  const formatDate = useFormatDate()

  const {
    loading,
    submitForm,
    confirm,
    onDelete,
    errors,
    control,
    watch,
    onChangeDate,
    triggerSubmitted,
    startDisabled,
    endDisabled,
    formStart,
    resourceDisabled,
    nights,
    resourceTypeResource,
    resourceTypes,
    assignableResources,
    onResourceTypeResourceChange,
    errorResourceMessage,
    currentPhase,
    inHouseOption,
    notYetArrivedOption,
    departedOption,
    initialPriceValue,
    calculateUniformPrice,
    setValue,
    isSubmitted,
    setDelta,
    initialAveragePriceValue,
  } = useEditRRDrawer({
    propertyId,
    reservedResource,
    isOnlyChild,
    onClose,
    open,
    isTimeResource: true,
  })

  return (
    <FormLayoutDrawer //
      open={open}
      onClose={onClose}
      title={t('pms.booking.reservation.time_reserved_resource_edit.title')}
      width={md ? '60%' : '100%'}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{
            disabled: loading,
            text: t('commons.save'),
            clickHandler: submitForm,
          }}
          cancelButton={{
            disabled: loading,
            text: t('commons.cancel'),
            clickHandler: onClose,
          }}
          deleteButton={{
            disabled: loading,
            text: t('commons.delete'),
            clickHandler: () => {
              confirm({
                title: t('pms.booking.reservation.reserved_resource_edit.delete.dialog_title'),
                message: t('pms.booking.reservation.reserved_resource_edit.delete.dialog_message'),
                confirmButtonText: t('commons.confirm'),
                cancelButtonText: t('commons.cancel'),
                onConfirm: onDelete,
              })
            },
          }}
        />
      }
      loading={loading}
    >
      <Grid container spacing={1}>
        {/* <div style={{ maxHeight: 300, overflow: 'auto', fontSize: 12 }}>
          <details>
            <summary style={{ color: 'red' }}>errors</summary>
            {JSON.stringify(errors)}
          </details>
          <details>
            <summary style={{ color: 'red' }}>form values</summary>
            {JSON.stringify(watch())}
          </details>
        </div> */}
        <Grid item xs={12} container spacing={1}>
          <Grid item xs={3}>
            <FormControl fullWidth error={errors?.start ? true : false}>
              <FormItem fieldLabel={t('pms.booking.reservation.reserved_resource_edit.checkin')} />
              <Controller<EditRRSchema, 'start'> //
                name={'start'}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <DatePicker //
                    value={value}
                    onChange={(v, k) => {
                      const formResourceOccupancies = [...(watch('resourceOccupancies') ?? [])]
                      onChangeDate(onChange, 'start', v, k, formResourceOccupancies)
                      triggerSubmitted()
                    }}
                    disabled={startDisabled}
                    shouldDisableDate={(day) => compareDates(day, hotelDate) < 0}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: errors?.start ? true : false,
                      },
                    }}
                  />
                )}
              />
              {(errors?.start?.message ? true : false) && <FormHelperText error>{errors?.start?.message}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth error={errors?.end ? true : false}>
              <FormItem fieldLabel={t('pms.booking.reservation.reserved_resource_edit.checkout')} />
              {/* <HicFormLabel error={errors?.end ? true : false}>{t('pms.booking.reservation.reserved_resource_edit.checkout')}</HicFormLabel> */}
              <Controller<EditRRSchema, 'end'> //
                name={'end'}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <DatePicker //
                    value={value}
                    onChange={(v, k) => {
                      const formResourceOccupancies = [...(watch('resourceOccupancies') ?? [])]
                      onChangeDate(onChange, 'end', v, k, formResourceOccupancies)
                      triggerSubmitted()
                    }}
                    disabled={endDisabled}
                    shouldDisableDate={(day) => (isValidDate(formStart) ? compareDates(day, formStart) <= 0 : compareDates(day, hotelDate) < 0)}
                    slotProps={{
                      textField: { size: 'small', error: errors.end ? true : false },
                    }}
                  />
                )}
              />
              {(errors?.end?.message ? true : false) && <FormHelperText error>{errors?.end?.message}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <Controller<EditRRSchema, 'time'>
              name={'time'}
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <HicTimeRangePicker
                    fieldSmall
                    maxWidth={md ? '130px' : undefined}
                    value={value ? { start: value.from, end: value.to } : null}
                    onChange={(v) => {
                      if (v) onChange({ from: v.start, to: v.end })
                    }}
                    startProps={{
                      startLabel: t('pms.commons.from'),
                      startDateError: errors.time?.from?.message,
                    }}
                    endProps={{
                      endLabel: t('pms.commons.to'),
                      endDateError: errors.time?.to?.message,
                    }}
                    timeUnitOfMeasurement={resourceTypeResource?.assignedResourceType?.timeConfiguration?.unitOfMeasurement ?? undefined}
                    activity={resourceTypeResource?.assignedResourceType?.timeConfiguration?.activity ?? undefined}
                    errors={errors.time ? true : false}
                  />
                )
              }}
            />
          </Grid>

          <Grid item xs={5}>
            <FormControl fullWidth error={errors?.resource ? true : false}>
              <FormItem fieldLabel={t('pms.booking.reservation.time_reserved_resource_edit.resource')} />
              <Controller<EditRRSchema, 'resource'> //
                name={'resource'}
                control={control}
                render={({ field: { onChange } }) =>
                  resourceDisabled ? (
                    <TextField //
                      value={`${resourceTypeResource?.resource?.code} (${
                        resourceTypeResource?.assignedResourceType && resourceTypeResource?.assignedResourceType.resourceTypeId !== resourceTypeResource?.resourceType.resourceTypeId
                          ? resourceTypeResource?.assignedResourceType.display
                          : resourceTypeResource?.resourceType.display
                      })`}
                      disabled
                      size="small"
                    />
                  ) : (
                    <ResourceTypeResourceSelect //
                      resourceTypes={resourceTypes}
                      assignableResources={assignableResources}
                      value={resourceTypeResource}
                      onChange={(v) => {
                        onResourceTypeResourceChange(onChange, v, watch('resourceOccupancies'))
                        triggerSubmitted()
                      }}
                      error={errors?.resource ? true : false}
                      disabled={resourceDisabled}
                      hideResourceTypeOptions
                    />
                  )
                }
              />
              {(errorResourceMessage ? true : false) && <FormHelperText error>{errorResourceMessage}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider light />
          </Grid>

          <Grid item xs={12} container spacing={1}>
            <Grid item xs="auto">
              <FormControl fullWidth error={errors?.averagePrice ? true : false}>
                <FormItem
                  error={errors?.averagePrice ? true : false}
                  fieldLabel={t('pms.booking.reservation.time_reserved_resource_edit.avg_price_night', { from: formatDate(watch('time.from'), 'p'), to: formatDate(watch('time.to'), 'p') })}
                />
                <Controller<EditRRSchema, 'averagePrice'> //
                  name={'averagePrice'}
                  control={control}
                  render={({ field: { value } }) => (
                    <ResettableTextField
                      disabled={loading}
                      changeOnBlur
                      initialValue={initialAveragePriceValue}
                      value={value ?? 0}
                      onChange={(v) => {
                        if (v !== null)
                          if (initialPriceValue === 0 && v !== 0) {
                            calculateUniformPrice(Math.round(v * nights))
                          } else {
                            const tmpCurrentPrice = v !== null ? Math.round(v * nights) : null
                            setValue('averagePrice', v === null ? null : Math.round(v), { shouldValidate: isSubmitted })
                            setValue('price', tmpCurrentPrice, { shouldValidate: isSubmitted })
                            if (initialPriceValue !== null && tmpCurrentPrice !== null) setDelta(tmpCurrentPrice === 0 ? 0 : tmpCurrentPrice / initialPriceValue)
                          }
                      }}
                      resetPosition={'RIGHT'}
                    />
                  )}
                />
                {(errors?.averagePrice?.message ? true : false) && <FormHelperText error>{errors?.averagePrice?.message}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs="auto">
              <FormControl fullWidth error={errors?.price ? true : false}>
                <FormItem error={errors?.price ? true : false} fieldLabel={t('pms.booking.reservation.time_reserved_resource_edit.price')} />
                <Controller<EditRRSchema, 'price'> //
                  name={'price'}
                  control={control}
                  render={({ field: { value } }) => (
                    <ResettableTextField
                      disabled={loading}
                      changeOnBlur
                      initialValue={initialPriceValue ?? 0}
                      value={value ?? 0}
                      onChange={(v) => {
                        if (initialPriceValue !== null && (v || v === 0)) {
                          if (initialPriceValue === 0 && v !== 0) {
                            calculateUniformPrice(v)
                          } else {
                            setValue('averagePrice', v === null ? null : v === 0 ? 0 : Math.round(v / nights), { shouldValidate: isSubmitted })
                            setValue('price', v === null ? null : v, { shouldValidate: isSubmitted })
                            setDelta(v === 0 ? 0 : v / initialPriceValue)
                          }
                        }
                      }}
                      resetPosition={'RIGHT'}
                    />
                  )}
                />
                {(errors?.price?.message ? true : false) && <FormHelperText error>{errors?.price?.message}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider light />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormItem fieldLabel={t('pms.booking.reservation.time_reserved_resource_edit.phase')} />
              <Stack direction="row" spacing={1} alignContent={'center'}>
                <Box display="flex">
                  <ReservedResourcePhaseCell phase={currentPhase} />
                </Box>
                <PhaseSelect //
                  inHouse={inHouseOption}
                  notYetArrived={notYetArrivedOption}
                  departed={departedOption}
                />
              </Stack>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </FormLayoutDrawer>
  )
}
